import { NoResultsWithButton, PreventTransitionPrompt, SearchInput } from "../../../components";
import { type SortingDirection, ViewType } from "../../../enums";
import UsersFileUploadModal from "components/usersFileUploadModal/UsersFileUploadModal";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import { Columns, getColumnOptions } from "./columnOptions";
import {
  AssignUsersToRoles,
  SucceedUserImport,
  UserManagerAddedSuccess,
  UserManagerDeleteAllSuccess,
} from "features/Application/services/realTimeNotification/events/people/peopleEvents";
import { type IObservable, type NotifyStepSettings } from "interfaces";
import { bindAction } from "interfaces/redux";
import { showGeneralLoaderWithTimeout } from "../../Application/actions/generalLoader/generalLoaderActions";
import * as backgroundTasksActionsRedux from "../../BackgroundTasks/state/backgroundTasksActions";
import { reset as resetPreview } from "../state/slices/csvImport/csvImportPreviewSlice";
import { reset as resetFileUpload, updateUploadProgress } from "../state/slices/csvImport/uploadCsvFileSlice";
import { fetchCountries, fetchManagedByDepartments, fetchManagedByJobTitles } from "../state/thunks/addUserThunk";
import { fetchUploadedFileColumns, fetchUsersImportPreviewData, uploadCsvFile } from "../state/thunks/csvImportThunk";
import CreateUserModal from "./CreateUserModal";
import RemoveUsersConfirmationModal from "features/Accounts/EditAccount/RemoveUsersConfirmationModal/RemoveUsersConfirmationModal";
import { fetchChildAccountRoles } from "features/Accounts/CreateAccount/state/thunks/createAccountThunk";
import type { NormalizedDropdown } from "utils/miscellaneousUtils";
import { bindActionCreators } from "redux";
import * as notificationsActionsRedux from "../../Notifications/state/notificationsActions";
import backgroundTask from "../../BackgroundTasks/backgroundTask";
import usersDataService from "../../Application/services/dataServices/usersDataService";
import { type RoleUser } from "interfaces/user";
import { pluralize } from "utils/stringUtils";
import { permissionsToAccountSelector } from "../EditAccount/state/slices/permissionsToAccountSlice";
import { noop } from "lodash";

export interface TemplateUsersOwnProps {
  editAccountId: number;
  users: RoleUser[];
  usersCount: number;
  isLoading: boolean;
  selectedIds: number[];
  isReadOnly?: boolean;
  showUsersFileUploadModal: boolean;
  renderAddUsersButton: (tooltipPosition?: string) => JSX.Element;
  onSelectedUsersChanged: (ids: number[]) => void;
  onCloseUsersFileUploadModal: () => void;
  fetchUsers: (skip: number, top: number, sortBy?: string, sortOrder?: SortingDirection) => void;
  onAddUserModalObserver: IObservable<(onConfirm: () => void) => void>;
  onDeleteUsersObserver: IObservable<(onConfirm: () => void, ids: number[]) => void>;
  deleteUsers: (userId: number[], accountId: number) => void;
  isNavigationEnabled?: boolean;

  onIsDirtyChange?: (isDirty: boolean) => void;
}

export type UserRoles = {
  userId: string;
  roleIds: string[];
};

export type TemplateUsersProps = TemplateUsersOwnProps & PropsFromRedux;
export type TemplateUsersRef = {
  reset: () => void;
  save: () => Promise<void>;
};

const TemplateUsers = forwardRef<TemplateUsersRef, TemplateUsersProps>((props, ref) => {
  const {
    users,
    usersCount,
    editAccountId,
    accountId,
    isLoading,
    fetchUsers,
    onSelectedUsersChanged,
    selectedIds,
    permissions,
    renderAddUsersButton,
    deleteUsers,
    onAddUserModalObserver,
    childRoles,
    isNavigationEnabled = true,
    isReadOnly = false,
    onIsDirtyChange,
    onDeleteUsersObserver,
    ...rest
  } = props;

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const onFileDisposed = useCallback(() => setUploadedFile(null), [setUploadedFile]);
  const [isDirty, setIsDirty] = useState(false);
  const [usersWithNewRoles, setUsersWithNewRoles] = useState<UserRoles[]>([]);

  useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  useEffect(() => {
    rest.onFetchChildRoles(editAccountId);
    rest.fetchManagedByDepartments(editAccountId);
    rest.fetchManagedByJobTitles(editAccountId);
    rest.fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = useCallback(
    (userId: number) => {
      onDeleteUsersObserver.notify(noop, [userId]);
    },
    [onDeleteUsersObserver],
  );

  const onDeleteConfirmed = useCallback(
    (ids: number[]) => {
      if (ids) {
        deleteUsers(ids, editAccountId);
      }
    },
    [deleteUsers, editAccountId],
  );

  const renderRemoveContactConfirmationModal = () => (
    <RemoveUsersConfirmationModal onTriggerModalObserver={onDeleteUsersObserver} onConfirmed={onDeleteConfirmed} />
  );

  const handleChangeRole = useCallback(
    (userId: string, value: NormalizedDropdown) => {
      setIsDirty(true);

      const user = users.find((u: RoleUser) => u.id.toString() === userId);
      if (!user) {
        return;
      }

      const newlySelectedRoleIds = value.selected.filter(
        (roleId) => !user.assignedRoles.some((role) => role.id === Number(roleId)),
      );

      if (newlySelectedRoleIds.length === 0) {
        setUsersWithNewRoles((prev) => prev.filter((item) => item.userId !== userId));
        return;
      }

      setUsersWithNewRoles((prev) => {
        const existing = prev.find((item) => item.userId === userId);
        if (existing) {
          return prev.map((item) => (item.userId === userId ? { ...item, roleIds: newlySelectedRoleIds } : item));
        }
        return [...prev, { userId, roleIds: newlySelectedRoleIds }];
      });
    },
    [users],
  );

  useImperativeHandle(ref, () => ({
    reset: () => {
      setUsersWithNewRoles([]);
      setIsDirty(false);
    },
    save: async () => {
      await onFinishAsync();
    },
  }));

  const onFinishAsync = useCallback(async () => {
    if (usersWithNewRoles.length === 0) {
      return;
    }
    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = props;

    const userCount = usersWithNewRoles.length;
    const roleCount = userCount > 1 ? userCount : usersWithNewRoles[0].roleIds.length;

    const userToRoleAssignments = usersWithNewRoles.flatMap((user) =>
      user.roleIds.map((roleId) => ({
        userId: Number(user.userId),
        roleId: Number(roleId),
      })),
    );

    setUsersWithNewRoles([]);
    setIsDirty(false);

    const params = {
      id: `AssignUsersToRole_${new Date()}`,
      title: `Assign ${pluralize("role", roleCount)} to ${pluralize("user", userCount)}`,
      getMessageIds: async () => {
        const response = await usersDataService.assignUsersToRolesV2(userToRoleAssignments, editAccountId);
        return [response.data];
      },
      successTransientMessage: `${pluralize("Role", roleCount)} ${roleCount > 1 ? "have" : "has"} been assigned to ${pluralize("user", userCount)} successfully`,
      failureTransientMessage: `Assigning ${pluralize("role", roleCount)} to ${pluralize("user", userCount)} failed!`,
    };

    if (params) {
      await backgroundTask.updateEntity(params, {
        addOperation: addOperationV1,
        sendTransientNotification,
      });
    }
  }, [usersWithNewRoles, props, editAccountId]);

  const columnOptions = useMemo(() => {
    if (usersWithNewRoles.length < 1) {
      setIsDirty(false);
    }

    return getColumnOptions({
      handleDelete,
      hasPermission: !isReadOnly,
      isReadOnly,
      selectedIds,
      moboAccountId: editAccountId !== accountId ? editAccountId : undefined,
      target: editAccountId !== accountId ? "_blank" : undefined,
      isNavigationEnabled,
      defaultRoles: childRoles.filter((x) => x.isDefault),
      handleChangeRole,
      usersWithNewRoles,
    });
  }, [
    usersWithNewRoles,
    handleDelete,
    isReadOnly,
    selectedIds,
    editAccountId,
    accountId,
    isNavigationEnabled,
    childRoles,
    handleChangeRole,
  ]);

  const startUsersImportHandler = useCallback(
    (fileName: string, mappedFields: { [key: string]: any }, notificationSettings?: NotifyStepSettings) => {
      return usersDataService.startUsersImportForAccount(fileName, editAccountId, mappedFields, notificationSettings);
    },
    [editAccountId],
  );

  const csvUsersUploadModal = useMemo(() => {
    const {
      actorId,
      csvImport,
      showUsersFileUploadModal,
      onCloseUsersFileUploadModal,
      backgroundTasks,
      addBackgroundTask,
      removeBackgroundTask,
      cancelBackgroundTask,
      showGeneralLoaderWithTimeout,
      uploadUsersFile,
      fetchUploadedFileColumns,
      fetchUsersImportPreviewData,
      resetFileUpload,
      updateUploadProgress,
      resetUsersImportPreviewData,
    } = rest;

    const { uploadStatus, csvColumns, preview } = csvImport;

    return (
      <UsersFileUploadModal
        showNotifyStep
        startUsersImport={startUsersImportHandler}
        showGeneralLoaderWithTimeout={showGeneralLoaderWithTimeout}
        addBackgroundTask={addBackgroundTask}
        removeBackgroundTask={removeBackgroundTask}
        cancelBackgroundTask={cancelBackgroundTask}
        accountId={accountId}
        actorId={actorId}
        showModal={showUsersFileUploadModal}
        onClose={onCloseUsersFileUploadModal}
        isFileUploading={uploadStatus.isUploading}
        droppedFile={uploadedFile}
        onFileDisposed={onFileDisposed}
        uploadProgress={uploadStatus.progress}
        uploadError={uploadStatus.error?.message ?? null}
        uploadUsersFile={uploadUsersFile}
        getUploadedFileColumns={fetchUploadedFileColumns}
        getUsersImportPreviewData={fetchUsersImportPreviewData}
        uploadedUsersFileColumns={csvColumns}
        cancelUsersFileUpload={resetFileUpload}
        resetUsersFileUpload={resetFileUpload}
        updateUsersFileUploadProgress={(...args: any[]) => updateUploadProgress(args[0])}
        usersImportPreviewData={preview}
        resetUsersImportPreviewData={resetUsersImportPreviewData}
        backgroundTasks={backgroundTasks}
      />
    );
  }, [rest, startUsersImportHandler, accountId, uploadedFile, onFileDisposed]);

  const renderPreventTransitionPrompt = (dirty: boolean) => {
    return (
      <PreventTransitionPrompt
        when={dirty}
        title="Exit Without Saving?"
        message="You have unsaved changes. Are you sure you want to exit? All changes will be lost."
      />
    );
  };

  return (
    <>
      <GenericItemsView
        viewType={ViewType.LIST}
        items={users}
        isLoading={isLoading}
        columnOptions={columnOptions}
        fetchData={fetchUsers}
        dataCount={usersCount}
        renderSearch={() => <SearchInput placeholder="Search for Users..." disabled />}
        // @ts-ignore
        noResultsContent={
          <NoResultsWithButton
            title="No Users"
            description="It looks like you don't have any users yet."
            iconClassName="fal fa-user"
            actionButton={renderAddUsersButton("top center")}
          />
        }
        selectedIds={selectedIds}
        isSelectDisabled={() => isReadOnly}
        onSelectedItemChanged={onSelectedUsersChanged}
        listViewRtnEvents={[
          SucceedUserImport,
          UserManagerAddedSuccess,
          UserManagerDeleteAllSuccess,
          AssignUsersToRoles,
        ]}
        permissions={permissions}
        defaultSortingColumnName={Columns.Added}
      />
      {csvUsersUploadModal}
      {renderRemoveContactConfirmationModal()}
      <CreateUserModal onTriggerModalObserver={onAddUserModalObserver} accountId={editAccountId} />
      {renderPreventTransitionPrompt(isDirty)}
    </>
  );
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { csvImport } = state.accounts.edit;
  const { creation } = state.accounts;
  return {
    accountId: state.userProfile.accountId,
    actorId: state.userProfile.id,
    csvImport,
    backgroundTasks: state.backgroundTasks.tasks,
    childRoles: creation.childRoles.items,
    permissions: permissionsToAccountSelector(state),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    addBackgroundTask: bindAction(backgroundTasksActionsRedux.addTask, dispatch),
    removeBackgroundTask: bindAction(backgroundTasksActionsRedux.deleteTask, dispatch),
    cancelBackgroundTask: bindAction(backgroundTasksActionsRedux.cancelTask, dispatch),
    showGeneralLoaderWithTimeout: bindAction(showGeneralLoaderWithTimeout, dispatch),
    uploadUsersFile: bindAction(uploadCsvFile, dispatch),
    fetchUploadedFileColumns: bindAction(fetchUploadedFileColumns, dispatch),
    fetchUsersImportPreviewData: bindAction(fetchUsersImportPreviewData, dispatch),
    resetFileUpload: bindAction(resetFileUpload, dispatch),
    updateUploadProgress: bindAction(updateUploadProgress, dispatch),
    resetUsersImportPreviewData: bindAction(resetPreview, dispatch),
    fetchManagedByDepartments: bindAction(fetchManagedByDepartments, dispatch),
    fetchManagedByJobTitles: bindAction(fetchManagedByJobTitles, dispatch),
    fetchCountries: bindAction(fetchCountries, dispatch),
    onFetchChildRoles: bindAction(fetchChildAccountRoles, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActionsRedux, dispatch),
    notificationsActions: bindActionCreators(notificationsActionsRedux, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(TemplateUsers);
export default ConnectedComponent;
